// Copyright (C) 2026 FlexiProof. All rights reserved.
//
// This source code file is a part of free software licensed under the terms of
// the MIT License as published by the Massachusetts Institute of Technology:
// you can use, copy, modify and distribute any part of it without limitation,
// subject to the conditions contained within that license.
//
// This source code file, and the software it forms a part of, IS PROVIDED "AS
// IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR IMPLIED. See the MIT License
// for more details.
//
// You should have received a copy of the MIT License along with this source
// code file in the root of this repository. If not, see one of the following
// 3rd party sites for a copy of the license template:
// - <https://opensource.org/licenses/MIT>
// - <https://choosealicense.com/licenses/mit>
// - <https://spdx.org/licenses/MIT>

/**
 * @file Vector2D class with methods required for constructing rectilinear
 * polygons.
 * @author James Reid
 */

// @ts-check

// @@no-imports

// @@body
/**
 * 2D vector class implementation providing:
 * - Standard vector addition and subtraction.
 * - Scalar vector multiplication and division.
 * - Vector dot multiplication.
 * - Angle calculations.
 * - Utility methods and properties relating to vector.
 */
class Vector2D {
    /** @type {number} */ #x
    /** @type {number} */ #y
    #direction = /** @type {?number} */ (null)
    #absoluteDirection = /** @type {?number} */ (null)
    #reverseDirection = /** @type {?number} */ (null)
    #isHorizontal = /** @type {?boolean} */ (null)
    #isVertical = /** @type {?boolean} */ (null)
    #isGridAligned = /** @type {?boolean} */ (null)
    #magnitude = /** @type {?number} */ (null)

    /**
     * Build vector from x and y coordinates of 2D vector.
     * @param {number} x Coordinate `x` of vector.
     * @param {number} y Coordinate `y` of vector.
     */
    constructor(x = NaN, y = NaN) {
        this.#x = x
        this.#y = y
    }

    /**
     * Update vector given optional updated `x` and `y` coordinates of vector.
     * Note that this method will also reset internal class properties.
     * @param {object} obj Optional destructured parameters object.
     * @param {number} [obj.x] New coordinate `x` of vector.
     * @param {number} [obj.y] New coordinate `y` of vector.
     */
    set({ x = this.#x, y = this.#y } = {}) {
        this.#x = x
        this.#y = y

        // Reset internal class properties such that they are re-calculated
        // when next accessed.
        this.#direction = null
        this.#absoluteDirection = null
        this.#reverseDirection = null
        this.#isHorizontal = null
        this.#isVertical = null
        this.#isGridAligned = null
        this.#magnitude = null
    }

    /**
     * Add another 2D vector to the current instance. By default performs
     * operation on new instance.
     * @param {Vector2D} vector Vector to add to the current vector instance.
     * @param {object} obj Optional destructured parameters object.
     * @param {boolean} [obj.inPlace] Should method update current instance of
     * vector *in place*, or should method return a new vector instance?
     * @returns {Vector2D} Vector result of addition.
     */
    add(vector, { inPlace = false } = {}) {
        const { x, y } = { x: this.#x + vector.x, y: this.#y + vector.y }
        if (inPlace) { this.set({ x, y }) }

        return inPlace ? this : new Vector2D(x, y)
    }

    /**
     * Subtract a 2D vector from the current instance. By default performs
     * operation on new instance.
     * @param {Vector2D} vector Vector to add to the current vector instance.
     * @param {object} obj Optional destructured parameters object.
     * @param {boolean} [obj.inPlace] Should method update current instance of
     * vector *in place*, or should method return a new vector instance?
     * @returns {Vector2D} Vector result of subtraction.
     */
    subtract(vector, { inPlace = false } = {}) {
        const { x, y } = { x: this.#x - vector.x, y: this.#y - vector.y }
        if (inPlace) { this.set({ x, y }) }

        return inPlace ? this : new Vector2D(x, y)
    }

    /**
     * Multiply current 2D vector instance by a scalar value. By default
     * performs operation on new instance.
     * @param {number} scalar Scalar value to multiply the current vector
     * instance by.
     * @param {object} obj Optional destructured parameters object.
     * @param {boolean} [obj.inPlace] Should method update current instance of
     * vector *in place*, or should method return a new vector instance?
     * @returns {Vector2D} Vector result of scalar multiplication.
     */
    scalarMultiply(scalar, { inPlace = false } = {}) {
        const { x, y } = { x: this.#x * scalar, y: this.#y * scalar }
        if (inPlace) { this.set({ x, y }) }

        return inPlace ? this : new Vector2D(x, y)
    }

    /**
     * Divide current 2D vector instance by a scalar value. By default performs
     * operation on new instance.
     * @param {number} scalar Scalar value to divide the current vector
     * instance by.
     * @param {object} obj Optional destructured parameters object.
     * @param {boolean} [obj.inPlace] Should method update current instance of
     * vector *in place*, or should method return a new vector instance?
     * @returns {Vector2D} Vector result of scalar division.
     */
    scalarDivide(scalar, { inPlace = false } = {}) {
        const { x, y } = { x: this.#x / scalar, y: this.#y / scalar }
        if (inPlace) { this.set({ x, y }) }

        return inPlace ? this : new Vector2D(x, y)
    }

    /**
     * Rotate current vertex instance by an angle in *radians* around an origin
     * point. By default performs operation on new instance.
     * @param {*} angle Angle of rotation in *radians*.
     * @param {object} obj Optional destructured parameters object.
     * @param {Vector2D} [obj.origin] Origin of rotation, defaults to (0,0).
     * @param {boolean} [obj.inPlace] Should method update current instance of
     * vector *in place*, or should method return a new vector instance?
     * @returns {Vector2D} Vector result of rotation.
     */
    rotate(angle, { origin = new Vector2D(0, 0), inPlace = false } = {}) {
        let { x, y } = this.subtract(origin);
        ({ x, y } = {
            x: x * Math.cos(angle) - y * Math.sin(angle) + origin.x,
            y: x * Math.sin(angle) + y * Math.cos(angle) + origin.y
        })
        if (inPlace) { this.set({ x, y }) }

        return inPlace ? this : new Vector2D(x, y)
    }

    /**
     * Normalize current 2D Vector instance such that returned vector has a
     * magnitude of 1 (creates unit vector). By default performs operation on
     * new instance.
     * @param {object} obj Optional destructured parameters object.
     * @param {boolean} [obj.inPlace] Should method update current instance of
     * vector *in place*, or should method return a new vector instance?
     * @returns {Vector2D} Normalized vector.
     */
    normalize({ inPlace = false } = {}) {
        return this.scalarDivide(this.magnitude, { inPlace })
    }

    /**
     * Dot multiply current 2D vector instance with another vector.
     * @param {Vector2D} vector Vector to dot multiply with current vector
     * instance.
     * @returns {number} Scalar result of dot multiplication.
     */
    dotMultiply(vector) {
        return this.#x * vector.x + this.#y * vector.y
    }

    /**
     * Calculate the angle *to* another vector *from* the current 2D vector
     * instance (i.e. calculate angle between vectors). Note that returned value
     * is supplied in radians.
     * @param {Vector2D} vector Vector to find angle *to* (*from* current
     * instance).
     * @returns {number} Angle to vector parameter in radians.
     */
    angleTo(vector) {
        // Calculate required components of angle calculation.
        const dotProduct = this.dotMultiply(vector)
        const magnitudeProduct = this.magnitude * vector.magnitude

        return Math.acos(dotProduct / magnitudeProduct)
    }

    /**
     * Evaluate if a given vector is parallel (same direction or reverse
     * direction) to the current vector instance.
     * @param {Vector2D} vector Vector to check alignment with current instance.
     * @returns {boolean} Are vectors aligned?
     */
    isParallelTo(vector) {
        return this.direction === vector.direction ||
            this.direction === vector.reverseDirection
    }

    /**
     * Evaluate if a given vector is equal (x and y coordinates the same) to the
     * current vector instance.
     * @param {Vector2D} vector Vector to compare to current instance.
     * @returns {boolean} Is vector argument equal to current 2D vector
     * instance?
     */
    equals(vector) {
        return this.#x === vector.x && this.#y === vector.y
    }

    /**
     * Copy current 2D vector instance to a new instance.
     * @returns {Vector2D} New vector instance copied from current instance.
     */
    copy() {
        return new Vector2D(this.#x, this.#y)
    }

    /**
     * Calculate orientation of 3 2D vector instances. Note that:
     * - The vectors may be considered to be vertices, and by comparing the
     * gradients between points A and B, and points B and C, we can infer the
     * orientation of the three points.
     * - Please see [here](https://www.geeksforgeeks.org/orientation-3-ordered-points/)
     * for more information on the details of the standard point orientation
     * algorithm, and for diagrams which better illustrate the idea of the
     * algorithm.
     * - This is  a trivial algorithm which can be derived from first
     * principles using comparisons of the gradients of the linear functions
     * which intersect successive pairs of points to determine orientation.
     * @param {Vector2D} vectorA Vector representing vertex A.
     * @param {Vector2D} vectorB Vector representing vertex B.
     * @param {Vector2D} vectorC Vector representing vertex C.
     * @returns {number} Number reflecting orientation of vertices in arguments:
     * - `-1` implies counterclockwise orientation of vertices.
     * - `0` implies colinear orientation of vertices.
     * - `+1` implies clockwise orientation of vertices.
     */
    static orientation(vectorA, vectorB, vectorC) {
        const { x: dXAtoB, y: dYAtoB } = vectorB.subtract(vectorA)
        const { x: dXBtoC, y: dYBtoC } = vectorC.subtract(vectorB)

        return Math.sign(dYAtoB * dXBtoC - dYBtoC * dXAtoB)
    }

    /**
     * Direction of 2D vector instance as elevation from the line y = 0 measured
     * in radians.
     */
    get direction() {
        if (this.#direction === null) {
            if (this.#x === 0 && this.#y === 0) { this.#direction = NaN }
            else { this.#direction = Math.atan2(this.#y, this.#x) }
        }

        return this.#direction
    }

    /**
     * Absolute direction of 2D vector instance as elevation from the line y = 0
     * measured in radians (i.e. vector direction corrected to be a positive
     * angle measurement).
     */
    get absoluteDirection() {
        if (this.#absoluteDirection === null) {
            this.#absoluteDirection = this.direction >= 0
                ? this.direction
                : 4 * Math.PI + this.direction
        }

        return this.#absoluteDirection
    }

    /**
     * Reverse or opposite direction of 2D vector instance as elevation from the
     * line y = 0 measured in radians. (i.e. vector direction plus pi radians or
     * 180 degrees).
     */
    get reverseDirection() {
        if (this.#reverseDirection === null) {
            this.#reverseDirection = this.direction > 0
                ? this.direction - Math.PI
                : this.direction + Math.PI
        }

        return this.#reverseDirection
    }

    /**
     * Is direction of the 2D vector instance aligned with the horizontal axis
     * (i.e. parallel to the line y = 0)?
     */
    get isHorizontal() {
        if (this.#isHorizontal === null) {
            this.#isHorizontal = !(this.direction % Math.PI)
        }

        return this.#isHorizontal
    }

    /**
     * Is direction of the 2D vector instance aligned with the vertical axis
     * (i.e. parallel to the line x = 0)?
     */
    get isVertical() {
        if (this.#isVertical === null) {
            this.#isVertical = Math.abs(this.direction) === Math.PI / 2
        }

        return this.#isVertical
    }

    /**
     * Is direction of 2D vector instance aligned with the grid (i.e. either
     * parallel to the line x = 0, or parallel to the line y = 0)?
     */
    get isGridAligned() {
        if (this.#isGridAligned === null) {
            this.#isGridAligned = this.isHorizontal || this.isVertical
        }

        return this.#isGridAligned
    }

    /**
     * Magnitude (length) of 2D vector instance.
     */
    get magnitude() {
        if (this.#magnitude === null) {
            this.#magnitude = Math.sqrt(this.#x * this.#x + this.#y * this.#y)
        }

        return this.#magnitude
    }

    /**
     * Coordinate `x` of vector.
     */
    get x() {
        return this.#x
    }

    /**
     * Coordinate `y` of vector.
     */
    get y() {
        return this.#y
    }
}

// @@exports
export { Vector2D }
